import React from "react";
import "./skills.css";
import Reveal from "../ui/Reveal";
import { useReveal } from "../../lib/useReveal";
import { stack } from "../../data/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

/** One string in an array: `"Python",` (the comma is dropped after the last one). */
const Items = ({ list }) =>
  list.map((name, i) => (
    <React.Fragment key={name}>
      <span className="code__item">
        <span className="tok-str">"{name}"</span>
        {i < list.length - 1 && <span className="tok-p">,</span>}
      </span>{" "}
    </React.Fragment>
  ));

/**
 * The stack as a file in an editor: `stack.toml`, one table per layer, `working` and `learning`
 * arrays. Lines type in one after another once the window scrolls into view. The text is real DOM,
 * so assistive tech reads it as ordinary content.
 */
const Skills = () => {
  const [ref, shown] = useReveal({ threshold: 0.12 });
  const total = stack.reduce((n, r) => n + r.use.length + r.learn.length, 0);
  const learning = stack.reduce((n, r) => n + r.learn.length, 0);

  // build the logical lines first so every line gets a number and a stagger index
  const lines = [];
  lines.push({ k: "comment", text: "# the tools and languages I work with, one table per layer" });
  stack.forEach((row) => {
    lines.push({ k: "blank" });
    lines.push({ k: "table", name: slug(row.layer), label: row.layer });
    lines.push({ k: "arr", key: "working", list: row.use });
    if (row.learn.length) lines.push({ k: "arr", key: "learning", list: row.learn, dim: true });
  });

  return (
    <section className="skills section" id="skills" aria-labelledby="skills-title">
      <div className="container">
        <Reveal as="header" className="section__head">
          <span className="eyebrow">04 — Skills</span>
          <h2 id="skills-title" className="section__title">
            Technology stack
          </h2>
          <p className="section__lead">Everything I work with, written the way I would declare it.</p>
        </Reveal>

        <div className="code" ref={ref} data-shown={shown}>
          <div className="code__bar" aria-hidden="true">
            <span className="code__dots">
              <i />
              <i />
              <i />
            </span>
            <span className="code__title">~/samartha/stack.toml</span>
            <span className="code__tag">TOML</span>
          </div>

          <ol className="code__body">
            {lines.map((l, i) => (
              <li key={i} className={`code__line code__line--${l.k}${l.dim ? " is-dim" : ""}`} style={{ "--i": i }}>
                {l.k === "comment" && <span className="tok-c">{l.text}</span>}
                {l.k === "table" && (
                  <>
                    <span className="tok-p">[</span>
                    <span className="tok-tbl">{l.name}</span>
                    <span className="tok-p">]</span>
                    <span className="sr-only"> {l.label}</span>
                  </>
                )}
                {l.k === "arr" && (
                  <>
                    <span className="tok-key">{l.key}</span> <span className="tok-p">=</span> <span className="tok-p">[</span>{" "}
                    <Items list={l.list} />
                    <span className="tok-p">]</span>
                  </>
                )}
                {l.k === "blank" && <span>&nbsp;</span>}
              </li>
            ))}
            <li className="code__line code__line--cursor" style={{ "--i": lines.length }} aria-hidden="true">
              <span className="code__caret" />
            </li>
          </ol>

          <div className="code__status" aria-hidden="true">
            <span>
              {stack.length} tables · {total} entries
            </span>
            {learning > 0 && (
              <span>
                {total - learning} working · {learning} learning
              </span>
            )}
            <span className="code__ok">● valid</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
